import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { ButtonWrapper, DropdownIcon, Placeholder, BKJSelectIcon, BKJSelectErrorMessage, BKJSelectPopoverWrapper, ClearButton, } from './BKJSelect.styled.js';
import { v4 as uuidv4 } from 'uuid';
import { usePopper } from 'react-popper';
import { getDesignLibraryElementNode } from './utils/getDesignLibraryElementNode.js';
import { useClickOutside } from '../../hooks/useClickOutside.js';
import { mergeRefs } from './utils/mergeRefs.js';
import { BKJDropdown } from '../BKJDropdown/index.js';
import { isArray } from '../../common.types.js';
import { ErrorBoundary } from '../ErrorBoundary/index.js';
import { BKJTheme } from '../../providers/index.js';
import { Portal } from '../Portal/Portal.js';
import { BKJIcon } from '../BKJIcon/index.js';
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_END, KEY_ENTER, KEY_ESCAPE, KEY_HOME, KEY_SPACE, VariantsWithClear, } from './BKJSelect.constants.js';
function getInitialActiveValue(options, selectedValues, direction) {
    const selectedOpt = options.find((opt) => selectedValues.includes(opt.value));
    const fallbackOpt = options.at(direction === 'down' ? 0 : -1);
    return (selectedOpt ?? fallbackOpt)?.value ?? null;
}
function getNextActiveValue(options, current, delta) {
    const currIdx = options.findIndex((opt) => opt.value === current);
    const nextIdx = Math.max(0, Math.min(options.length - 1, currIdx + delta));
    return options[nextIdx]?.value ?? null;
}
function resolveAriaLabel(ariaLabelledBy, ariaLabel, fallback) {
    if (ariaLabelledBy !== undefined)
        return undefined;
    return ariaLabel ?? fallback;
}
function BKJSelectEndIcon({ iconProps, isOpen, value, name, variant, onChange, disabled, }) {
    if (variant === 'PurpleTextNoArrow')
        return null;
    const handleClear = (e) => {
        e.stopPropagation();
        onChange({ target: { value: [], name } });
    };
    const handleClearKeyDown = (e) => {
        if (disabled)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleClear(e);
        }
    };
    if (value.length >= 1 && VariantsWithClear.includes(variant)) {
        return (_jsx(ClearButton, { role: "button", tabIndex: disabled ? -1 : 0, "aria-label": "Clear selection", onClick: handleClear, onKeyDown: handleClearKeyDown, children: _jsx(BKJIcon, { iconName: "CloseBold", color: "White" }) }));
    }
    return (_jsx(DropdownIcon, { ...iconProps, iconName: "ArrowDown", color: BKJTheme.select[variant].colorScheme.fontColor(isOpen), isOpen: isOpen }));
}
function BKJSelectComponent(props, ref) {
    const { type = 'Single', label, name, onChange, onClose, value: _value = [], placeholder, required = false, variant = 'GreyOutlined', width = 'auto', disabled, dropdownVariant = 'Square', dropdownPlacement = 'bottom', moreValuesLabel = 'More', dropdownWidth, dropdownHeight, dropdownMinHeight, options, renderOptions: RenderOptions, iconComponent: IconComponent, iconProps = {}, modifiers: _modifiers, error, dropdownProps, placeholderColor, headerSlot, 'aria-label': ariaLabelFromRest, 'aria-labelledby': ariaLabelledByFromRest, 'aria-describedby': consumerDescribedBy, ...rest } = props;
    const errorMessages = (Array.isArray(error) ? error : [error]).filter((e) => typeof e === 'string' && e.length > 0);
    const isError = typeof error === 'boolean' ? error : errorMessages.length > 0;
    if (!Array.isArray(_value))
        throw new Error("BKJSelect must receive an array for it's value. Even if type is set to Single.");
    const value = useMemo(() => (Boolean(_value.length) ? _value : []), [_value]);
    // Per-value label overrides captured from click events (event.target.label).
    // Consumers that translate labels at renderOptions time emit a label that
    // differs from options[].label; without preserving it, the trigger falls back
    // to the raw key. External value updates (e.g., Select All bypassing onChange)
    // still work because the override map only contains values the user clicked.
    const [labelOverrides, setLabelOverrides] = useState({});
    const displayValues = useMemo(() => value
        .map((v) => {
        if (labelOverrides[v] !== undefined)
            return labelOverrides[v];
        return options.find((opt) => opt.value === v)?.label;
    })
        .filter((l) => typeof l === 'string' && l.length > 0), [options, value, labelOverrides]);
    const modifiers = useMemo(() => _modifiers || [
        {
            name: 'offset',
            options: {
                offset: () => {
                    switch (dropdownVariant) {
                        case 'SeamlessRounded':
                            return [0, -2];
                        case 'Square':
                            return [0, 4];
                        default:
                            return [0, 0];
                    }
                },
            },
        },
        // Use top/left positioning instead of transform so the popover wrapper
        // does not become a stacking context — lets BKJDropdown's z-index reach
        // the body's stacking context without needing a duplicated z-index here.
        { name: 'computeStyles', options: { gpuAcceleration: false } },
        {
            name: 'sameWidth',
            enabled: !dropdownWidth,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn({ state }) {
                if (dropdownWidth)
                    return void 0;
                state.styles.popper.minWidth = `${state.rects.reference.width}px`;
                state.styles.popper.maxWidth = `${state.rects.reference.width}px`;
            },
            effect({ state }) {
                if (dropdownWidth)
                    return void 0;
                state.elements.popper.style.minWidth = `${state.elements.reference.offsetWidth}px`;
                state.elements.popper.style.maxWidth = `${state.elements.reference.offsetWidth}px`;
            },
        },
    ], [dropdownVariant, _modifiers]);
    const listboxId = useMemo(() => `bkj-select-listbox-${uuidv4()}`, []);
    const optionIdsByValue = useMemo(() => {
        const map = new Map();
        for (const opt of options)
            map.set(String(opt.value), `bkj-select-option-${uuidv4()}`);
        return map;
    }, [options]);
    const errorMessageIds = useMemo(() => errorMessages.map(() => `bkj-select-error-${uuidv4()}`), [errorMessages.length]);
    const describedByIds = [consumerDescribedBy, ...errorMessageIds].filter(Boolean).join(' ') || undefined;
    const resolvedAriaLabel = resolveAriaLabel(ariaLabelledByFromRest, ariaLabelFromRest, label);
    const buttonRef = useRef();
    const dropdownRef = useRef();
    const [inputElement, setInputElement] = useState();
    const [dropdownElement, setDropdownElement] = useState();
    const [targetElement, setTargetElement] = useState(getDesignLibraryElementNode('dropdown-root'));
    const [isOpen, setOpen] = useState(false);
    const [activeValue, setActiveValue] = useState(null);
    const activeOptionId = activeValue !== null ? optionIdsByValue.get(activeValue) : undefined;
    useEffect(() => {
        if (!targetElement) {
            const element = document.createElement('div');
            element.setAttribute('id', 'dropdown-root');
            document.body.prepend(element);
            setTargetElement(element);
        }
    }, []); // eslint-disable-line
    useEffect(() => {
        const stillPresent = options.some((opt) => opt.value === activeValue);
        if (!isOpen || activeValue === null || stillPresent)
            return;
        setActiveValue(options.at(0)?.value ?? null);
    }, [options, isOpen, activeValue]);
    const handleChange = useCallback((event) => {
        switch (type) {
            case 'Multiple': {
                if (Array.isArray(event.target.value)) {
                    if (Array.isArray(event.target.label)) {
                        setLabelOverrides(() => {
                            const next = {};
                            event.target.value.forEach((v, i) => {
                                if (typeof event.target.label[i] === 'string')
                                    next[v] = event.target.label[i];
                            });
                            return next;
                        });
                    }
                    onChange({
                        ...event,
                        target: { ...event.target, value: [...new Set(event.target.value)] },
                    });
                    return;
                }
                const nextValue = value.includes(event.target.value)
                    ? value.filter((v) => v !== event.target.value)
                    : [...value, event.target.value];
                if (typeof event.target.label === 'string') {
                    setLabelOverrides((curr) => ({ ...curr, [event.target.value]: event.target.label }));
                }
                onChange({ ...event, target: { ...event.target, value: nextValue } });
                break;
            }
            case 'Single': {
                if (typeof event.target.label === 'string') {
                    setLabelOverrides({ [event.target.value]: event.target.label });
                }
                onChange({ ...event, target: { ...event.target, value: [event.target.value] } });
                setOpen(false);
                setActiveValue(null);
                buttonRef.current?.focus();
                break;
            }
        }
    }, [onChange, type, value]);
    const handleToggle = useCallback(() => {
        if (disabled)
            return;
        const willOpen = !isOpen;
        setOpen(willOpen);
        setActiveValue(willOpen ? getInitialActiveValue(options, value, 'down') : null);
    }, [disabled, isOpen, options, value]);
    const handleClose = useCallback((event) => {
        const clickedInsideTrigger = buttonRef.current?.contains(event.target);
        const clickedInsideDropdown = dropdownRef.current?.contains(event.target);
        if (clickedInsideTrigger || clickedInsideDropdown)
            return;
        setOpen(false);
        setActiveValue(null);
        onClose?.(event);
    }, [onClose]);
    const handleKeyDown = useCallback((e) => {
        if (disabled)
            return;
        const { key } = e;
        if (!isOpen) {
            const opensOnKey = key === KEY_SPACE || key === KEY_ENTER || key === KEY_ARROW_DOWN || key === KEY_ARROW_UP;
            if (!opensOnKey)
                return;
            e.preventDefault();
            setOpen(true);
            setActiveValue(getInitialActiveValue(options, value, key === KEY_ARROW_UP ? 'up' : 'down'));
            return;
        }
        switch (key) {
            case KEY_ARROW_DOWN:
                e.preventDefault();
                setActiveValue((curr) => getNextActiveValue(options, curr, +1));
                break;
            case KEY_ARROW_UP:
                e.preventDefault();
                setActiveValue((curr) => getNextActiveValue(options, curr, -1));
                break;
            case KEY_HOME:
                e.preventDefault();
                setActiveValue(options.at(0)?.value ?? null);
                break;
            case KEY_END:
                e.preventDefault();
                setActiveValue(options.at(-1)?.value ?? null);
                break;
            case KEY_ENTER:
            case KEY_SPACE: {
                e.preventDefault();
                const opt = options.find((o) => o.value === activeValue);
                if (!opt)
                    return;
                handleChange({ target: { name, value: opt.value, label: opt.label } });
                break;
            }
            case KEY_ESCAPE:
                e.preventDefault();
                setOpen(false);
                setActiveValue(null);
                buttonRef.current?.focus();
                break;
        }
    }, [disabled, isOpen, options, value, activeValue, name, handleChange]);
    const { styles, attributes } = usePopper(inputElement, dropdownElement, {
        modifiers,
        placement: dropdownPlacement,
    });
    const popperStyles = useMemo(() => ({
        ...styles['popper'],
        maxHeight: 'auto',
        height: 'auto',
        minHeight: 'auto',
    }), [styles]);
    useClickOutside([buttonRef && buttonRef.current, dropdownRef && dropdownRef.current, dropdownElement], handleClose);
    const popperProps = useMemo(() => ({
        style: popperStyles,
        ...attributes['popper'],
    }), [attributes, popperStyles]);
    let isPlaceholderShown = Boolean(value.length === 0) && !!placeholder;
    if (isArray(value)) {
        isPlaceholderShown = Boolean(value.length === 0) && !!placeholder;
    }
    if (isArray(value) && value.length === 1 && value[0] === '') {
        isPlaceholderShown = !!placeholder;
    }
    if (typeof value === 'string') {
        isPlaceholderShown = value === '' && !!placeholder;
    }
    const scrollRef = useRef();
    const RenderOptionsComponent = RenderOptions;
    return (_jsxs(ErrorBoundary, { componentName: "BKJSelect", height: "38px", direction: "row", size: "Small", width: width, isStyled: true, children: [_jsxs(ButtonWrapper, { ...rest, disabled: disabled, "$isPlaceholderShown": isPlaceholderShown, ref: mergeRefs([ref, buttonRef, setInputElement]), name: name, onClick: handleToggle, onKeyDown: handleKeyDown, value: value, variant: variant, dropdownVariant: dropdownVariant, width: width, isOpen: isOpen, isError: isError, type: "button", role: "combobox", "aria-haspopup": "listbox", "aria-expanded": isOpen, "aria-controls": isOpen ? listboxId : undefined, "aria-activedescendant": isOpen ? activeOptionId : undefined, "aria-label": resolvedAriaLabel, "aria-labelledby": ariaLabelledByFromRest, "aria-invalid": isError ? true : undefined, "aria-describedby": describedByIds, "aria-required": required ? true : undefined, children: [IconComponent && iconProps.iconName && _jsx(BKJSelectIcon, { ...iconProps }), isPlaceholderShown ? (_jsx(Placeholder, { placeholderColor: placeholderColor, children: placeholder })) : typeof label === 'undefined' ? (_jsx("span", { children: displayValues.length > 0
                            ? `${displayValues[0]}${displayValues.length > 1
                                ? `... +${displayValues.length - 1} ${moreValuesLabel}`
                                : ''}`
                            : placeholder })) : (_jsx("span", { children: label })), _jsx(BKJSelectEndIcon, { iconProps: iconProps, isOpen: isOpen, value: value, name: name, variant: variant, disabled: disabled, onChange: (e) => {
                            setLabelOverrides({});
                            return onChange(e);
                        } })] }), errorMessages.map((err, idx) => (_jsx(BKJSelectErrorMessage, { id: errorMessageIds[idx], children: err }, err))), targetElement && isOpen && (_jsx(Portal, { targetElement: targetElement, children: _jsxs(BKJSelectPopoverWrapper, { ...popperProps, ref: mergeRefs([dropdownRef, setDropdownElement]), children: [headerSlot, _jsx(BKJDropdown, { "$dropdownVariant": dropdownVariant, "$dropdownWidth": dropdownWidth, "$dropdownHeight": dropdownHeight, "$dropdownMinHeight": dropdownMinHeight, role: "listbox", id: listboxId, "aria-multiselectable": type === 'Multiple' ? true : undefined, children: _jsx(RenderOptionsComponent, { scrollRef: scrollRef, onChange: handleChange, selected: value, options: options, onClose: handleClose, name: name, type: type, dropdownProps: dropdownProps, optionIdsByValue: optionIdsByValue }) })] }) }))] }));
}
export const BKJSelect = forwardRef(BKJSelectComponent);
//# sourceMappingURL=BKJSelect.js.map